import { useState } from "react";
import { faBoxesPacking, faPoundSign, faStar, faUsers, faPalette } from "@fortawesome/free-solid-svg-icons";
import { faChartLineUp } from "@fortawesome/pro-solid-svg-icons";
import { getLoginType, getUserFullName, getUserResellerType, ResellerType } from "../../utils/getUserInformation";
import useLocalStorage from "../../hooks/useLocalStorage";
import { useSession, LogoutReason } from "../Auth/hooks/useSession";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import IconCard from "../../components/iconCard/IconCard";
import GetStartedNew from "./GetStartedNew";
import PermissionGate, { PermissionType } from "../../features/Permissions/PermissionGate";
import "./Home.scss";

const Home = () => {
	const { reason } = useSession();
	const [resellerType, setResellerType] = useState(ResellerType.Invalid);
	const [showGetStartedNewModal, setGetStartedNewModal] = useState(false);
	const [user, setUser] = useState("");
	const [reloadLocalStorage, setReloadLocalStorage] = useState(false);

	useLocalStorage("ap-liu", () => {
		if (getLoginType() === 1) {
			setGetStartedNewModal(true);
		}
		setUser(getUserFullName());
		setResellerType(getUserResellerType());
	}, reloadLocalStorage);


	const description = (() => {
		switch (resellerType) {
			case ResellerType.iTEL:
				return "This site contains everything you need to manage your Resellers' access to the ucSKILLS digital onboarding platform, and review your billing. " +
					"Select one of the links below to get started.";
			case ResellerType.Billable:
				return "This site contains everything you need to provision your users on ucSKILLS, manage your Resellers' access to the ucSKILLS digital onboarding platform, " +
					"and review your billing. Select one of the links below to get started.";
			case ResellerType.NonBillable:
				return "Here you can manage your Resellers and set your company's branding (white-labelling) preferences.";
			default:
				return "";
		}
	})();

	return (
		<div>
			<h1>Welcome {user}</h1>
			{(!showGetStartedNewModal && reason !== LogoutReason.None)
				? <AuthLogoutMessage reason={reason} />
				: (<>
					{showGetStartedNewModal && <GetStartedNew showModal={showGetStartedNewModal} setModal={setGetStartedNewModal} reload={setReloadLocalStorage} />}
					<p className="mb-6">{description}</p>
					<div className="mt-4 twoColGrid">
						<PermissionGate all={[PermissionType.ManageUcSkillsAccess]} when={resellerType === ResellerType.Billable}>
							<IconCard title="ucSKILLS Access" icon={faStar} link="/customers">
								Give access to ucSKILLS to your customers or sub-resellers. Here you can provision users on ucSKILLS and set their offer.
							</IconCard>
						</PermissionGate>
						<PermissionGate all={[PermissionType.ManageUcSkillsAccess]} when={resellerType === ResellerType.NonBillable}>
							<IconCard title="My ucSKILLS Customers" icon={faStar} link="/customers">
								Here you can view your customer's details.
							</IconCard>
						</PermissionGate>

						<PermissionGate any={[PermissionType.ManageAdminUsers]}>
							<IconCard title="My Team" icon={faUsers} link="/teams">
								Manage your team - add or remove access to the Admin Portal for those who need it across your organisation.
							</IconCard>
						</PermissionGate>

						<PermissionGate any={[PermissionType.ManageResellers]}>
							<IconCard title="My Resellers" icon={faBoxesPacking} link="/resellers">
								{resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable
									? "Set up your resellers on the ucSKILLS Admin Portal. This will allow you to provision customers for them via the API, let them set their white labelling for ucSKILLS, and allow them to see their customers."
									: "Set up your resellers on the ucSKILLS Admin Portal. This will allow them to set their white labelling for ucSKILLS and allow them to see their customers."}
							</IconCard>
						</PermissionGate>
						<PermissionGate all={[PermissionType.ViewReports]} when={resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable}>
							<IconCard title="My Billing" icon={faPoundSign} link="/billing">
								Download your monthly invoices and set an email address to receive them automatically. Dig into your sales figures and those of your resellers to
								understand your billing and performance.
							</IconCard>
						</PermissionGate>
						{resellerType === ResellerType.iTEL &&
							<IconCard title="Provisioning" icon={faStar} link="/provisioning">
								Provision new ucSKILLS Companies from CRM data.
							</IconCard>
						}
						<PermissionGate any={[PermissionType.SetWhiteLabelling]} when={resellerType === ResellerType.NonBillable || resellerType === ResellerType.Billable}>
							<IconCard title="White Labelling" icon={faPalette} link="/white-labelling">
								Set the look and feel for you and your customers.
							</IconCard>
						</PermissionGate>
						<PermissionGate all={[PermissionType.AccessInvoices]} when={resellerType === ResellerType.iTEL || resellerType === ResellerType.Billable}>
							<IconCard title="My Reports" icon={faChartLineUp} link="/reports">
								View your reports.
							</IconCard>
						</PermissionGate>
					</div>
				</>)
			}
		</div>
	);
};

export default Home;